import { useDispatch, useSelector } from "react-redux";

import GenericSpinner from "@kikoff/components/src/v1/animations/GenericSpinner";
import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import TextInput from "@kikoff/components/src/v1/inputs/TextInput";
import { cls } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import {
  FormattedOption,
  FormattedSurveyData,
  selectAnswer,
  selectEmbeddedAnswer,
  selectFreeformAnswer,
  selectQuestion,
  setAnswer,
  setEmbeddedAnswer,
  setFreeformAnswer,
} from "@feature/hdyhau_survey";
import { track } from "@util/analytics";

import hdyhauSelectedCheck from "../images/hdyhau_selected_check.svg";

import styles from "./rows.module.scss";

interface OptionProps {
  option: FormattedOption;
  selected: boolean;
}

function Option({ option, selected }: OptionProps) {
  const dispatch = useDispatch();
  const showEmbeddedQuestion = selected && option.nextQuestion;
  const showSelected = selected && !showEmbeddedQuestion;

  return (
    <ContainerButton
      className={cls(styles["option"], showSelected && styles["selected"])}
      onClick={() => {
        track.tap("HDYHAU - Option", {
          option: option?.value || "not_found",
        });
        dispatch(setEmbeddedAnswer(null));
        dispatch(setAnswer(option));
      }}
    >
      <div className="row align-center justify-between w-full">
        <div className="row justify-between align-center w-full">
          <div className="text:small+ text-center">{option.value}</div>
          {option.iconUrl && <img src={option.iconUrl} alt={option.value} />}
        </div>
      </div>
      {showEmbeddedQuestion && (
        <Question embedded question={option.nextQuestion} />
      )}
    </ContainerButton>
  );
}

function OptionEmbedded({ option, selected }: OptionProps) {
  const dispatch = useDispatch();
  const freeformAnswer = useSelector(selectFreeformAnswer());

  return (
    <ContainerButton
      className={cls(styles["option-embedded"], selected && styles["selected"])}
      onClick={(e) => {
        track.tap("HDYHAU - Suboption", {
          suboption: option?.value || "not_found",
        });
        e.stopPropagation();
        dispatch(setEmbeddedAnswer(option));
        if (option.type === "freeform") {
          setTimeout(() => {
            (e.target as HTMLElement).scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 100);
        }
      }}
    >
      <div className="row align-center w-full justify-between">
        <div className="text:small+">{option.value}</div>
        {selected && (
          <img
            className={styles["option-embedded-version-2-checkmark"]}
            height={16}
            width={16}
            src={hdyhauSelectedCheck.src}
            alt="Checkmark"
          />
        )}
      </div>
      {selected && option.type === "freeform" && (
        <TextInput
          autoFocus
          className="text:small+ pt-0.5"
          variant="outlined"
          placeholder={option.placeholder}
          value={freeformAnswer}
          onChange={(e) => {
            dispatch(setFreeformAnswer(e.target.value));
          }}
        />
      )}
    </ContainerButton>
  );
}

interface QuestionProps {
  question: FormattedSurveyData;
  embedded?: boolean;
}

function Question({ embedded = false, question }: QuestionProps) {
  const answer = useSelector(selectAnswer());
  const embeddedAnswer = useSelector(selectEmbeddedAnswer());

  return (
    <>
      {!embedded && <h1 className="text:large+ m-0 p-3">{question?.prompt}</h1>}
      <div className={cls(styles["option-container"], embedded && "embedded")}>
        {embedded && (
          <h3 className="pb-0.5 text:small m-0 color:moderate pt-2">
            {question?.prompt}
          </h3>
        )}
        {[...question.radioOptions, ...question.freeformOptions].map(
          (option) => {
            return embedded ? (
              <OptionEmbedded
                key={option.key}
                option={option}
                selected={embeddedAnswer?.key === option.key}
              />
            ) : (
              <Option
                key={option.key}
                option={option}
                selected={answer?.key === option.key}
              />
            );
          }
        )}
      </div>
    </>
  );
}

export default function () {
  const [question, loading] = useSelector(selectQuestion.load());

  if (loading) return <GenericSpinner />;

  return (
    <PageAlign className={styles["onboarding-survey-view"]} overscroll>
      <Question question={question} />
    </PageAlign>
  );
}
